import { HttpApiService } from "@/shared/services/httpApi.service";

import type {
  CreateOtcOrderPayload,
  CreateOtcQuotePayload,
  CreateOtcSavedWalletPayload,
  OtcOrder,
  OtcOrdersPage,
  OtcOrdersQuery,
  OtcPage,
  OtcPair,
  OtcQuote,
  OtcSavedWallet,
  OtcTiming,
  OtcTimingEstimate,
  OtcVolumeDiscount,
  OtcVolumeDiscountPayload,
  OtcVolumeSuggestion,
} from "../types/cryptoSwaps.types";

const BASE_URL = "/internal/otc";

/**
 * In-flight order creations, keyed by idempotency key. React StrictMode double-fires effects, and the
 * backend's idempotency lock answers a concurrent duplicate with a 409 the user would read as a failure.
 */
const inFlightOrderCreations = new Map<string, Promise<OtcOrder>>();

export function normalizeOtcPairs(payload: unknown): OtcPair[] {
  const source = (payload as { pairs?: unknown } | null | undefined)?.pairs;
  if (!Array.isArray(source)) return [];
  return source.filter(
    (candidate): candidate is OtcPair =>
      !!candidate &&
      typeof candidate === "object" &&
      typeof (candidate as OtcPair).pair === "string" &&
      typeof (candidate as OtcPair).send_currency === "string" &&
      typeof (candidate as OtcPair).receive_currency === "string",
  );
}

function readTimingSide(source: unknown): Partial<Record<string, OtcTimingEstimate>> {
  const side: Partial<Record<string, OtcTimingEstimate>> = {};
  if (!Array.isArray(source)) return side;
  for (const row of source as Array<Record<string, unknown> | null>) {
    const estimatedSeconds = Number(row?.estimated_seconds);
    const sampleSize = Number(row?.sample_size);
    if (typeof row?.network !== "string" || !Number.isFinite(estimatedSeconds) || estimatedSeconds <= 0) {
      continue;
    }
    const trend =
      row.trend === "faster" || row.trend === "slower" || row.trend === "stable" ? row.trend : null;
    side[row.network] = {
      estimatedSeconds,
      sampleSize: Number.isFinite(sampleSize) ? sampleSize : 0,
      trend,
    };
  }
  return side;
}

export function normalizeOtcTiming(payload: unknown): OtcTiming {
  const source = (payload ?? {}) as { buy?: unknown; sell?: unknown };
  return { buy: readTimingSide(source.buy), sell: readTimingSide(source.sell) };
}

function readSuggestion(source: unknown): OtcVolumeSuggestion | null {
  const row = (source ?? {}) as Partial<Record<keyof OtcVolumeSuggestion, unknown>>;
  const fields = [
    row.kind,
    row.send_amount,
    row.debit_amount,
    row.receive_amount,
    row.price,
    row.savings_per_unit,
    row.extra_receive,
  ];
  if (!fields.every((value) => typeof value === "string")) return null;
  return row as OtcVolumeSuggestion;
}

export function normalizeOtcVolumeDiscount(payload: unknown): OtcVolumeDiscount | null {
  const source = (payload ?? {}) as Record<string, unknown>;
  if (typeof source.id !== "string" || typeof source.quote_id !== "string") return null;
  const meter = (source.discount ?? null) as Record<string, unknown> | null;
  const discount =
    meter &&
    ["percent", "per_unit", "min_amount", "min_price", "best_price"].every(
      (key) => typeof meter[key] === "string",
    )
      ? (meter as unknown as OtcVolumeDiscount["discount"])
      : null;
  const suggestions = Array.isArray(source.suggestions)
    ? source.suggestions.map(readSuggestion).filter((s): s is OtcVolumeSuggestion => s !== null)
    : [];
  return {
    id: source.id,
    quote_id: source.quote_id,
    side: source.side === "SELL" ? "SELL" : "BUY",
    show: source.show === true,
    currency: typeof source.currency === "string" ? source.currency : "",
    asset: typeof source.asset === "string" ? source.asset : "",
    send_amount: typeof source.send_amount === "string" ? source.send_amount : "0",
    balance_available: typeof source.balance_available === "string" ? source.balance_available : null,
    insufficient_balance: source.insufficient_balance === true,
    discount,
    suggestions,
    top_up: readSuggestion(source.top_up),
  };
}

export function readOtcPage<T>(payload: unknown, query: { skip?: number; limit?: number }): OtcPage<T> {
  const source = (payload ?? {}) as Partial<OtcPage<T>>;
  const rows = Array.isArray(source.data) ? source.data : [];
  return {
    data: rows,
    total: typeof source.total === "number" ? source.total : rows.length,
    skip: typeof source.skip === "number" ? source.skip : (query.skip ?? 0),
    limit: typeof source.limit === "number" ? source.limit : (query.limit ?? rows.length),
    hasMore: source.hasMore === true,
  };
}

/** Drops empty params: the backend rejects unknown or blank query fields (`forbidNonWhitelisted`). */
export function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export const CryptoSwapsService = {
  /** Whether the organization may buy and sell USDT. Staff may ask for one with `organizationId`. */
  async access(organizationId?: string | null): Promise<boolean> {
    const { data } = await HttpApiService.get<{ enabled?: unknown }>(
      `${BASE_URL}/access${buildQuery({ organization_id: organizationId })}`,
    );
    return data?.enabled === true;
  },

  async timing(): Promise<OtcTiming> {
    const { data } = await HttpApiService.get<unknown>(`${BASE_URL}/timing`);
    return normalizeOtcTiming(data);
  },

  async listPairs(organizationId?: string | null): Promise<OtcPair[]> {
    const { data } = await HttpApiService.get<unknown>(
      `${BASE_URL}/pairs${buildQuery({ organization_id: organizationId })}`,
    );
    return normalizeOtcPairs(data);
  },

  async createQuote(payload: CreateOtcQuotePayload, signal?: AbortSignal): Promise<OtcQuote> {
    const { data } = await HttpApiService.post<OtcQuote>(`${BASE_URL}/quotes`, payload, { signal });
    if (!data?.id) throw new Error("otc: the quote response carries no id");
    return data;
  },

  async volumeDiscount(
    quoteId: string,
    payload: OtcVolumeDiscountPayload,
    signal?: AbortSignal,
  ): Promise<OtcVolumeDiscount | null> {
    const { data } = await HttpApiService.post<unknown>(
      `${BASE_URL}/quotes/${quoteId}/volume-discount`,
      payload,
      { signal },
    );
    return normalizeOtcVolumeDiscount(data);
  },

  /** Never call twice for one attempt: the quote is single-use. Use {@link createOrderOnce}. */
  async createOrder(payload: CreateOtcOrderPayload): Promise<OtcOrder> {
    const { data } = await HttpApiService.post<OtcOrder>(`${BASE_URL}/orders`, payload, {
      headers: { "Idempotency-Key": payload.idempotency_key },
    });
    return data;
  },

  /** One creation per idempotency key. A failed attempt stays retryable with the same key. */
  createOrderOnce(payload: CreateOtcOrderPayload): Promise<OtcOrder> {
    const key = payload.idempotency_key;
    const existing = inFlightOrderCreations.get(key);
    if (existing) return existing;

    const request = CryptoSwapsService.createOrder(payload).finally(() => {
      inFlightOrderCreations.delete(key);
    });
    inFlightOrderCreations.set(key, request);
    return request;
  },

  async getOrder(orderId: string, signal?: AbortSignal): Promise<OtcOrder> {
    const { data } = await HttpApiService.get<OtcOrder>(`${BASE_URL}/orders/${encodeURIComponent(orderId)}`, {
      signal,
    });
    return data;
  },

  async listOrders(query: OtcOrdersQuery, signal?: AbortSignal): Promise<OtcOrdersPage> {
    const { data } = await HttpApiService.get<unknown>(`${BASE_URL}/orders${buildQuery({ ...query })}`, {
      signal,
    });
    return readOtcPage<OtcOrder>(data, query);
  },

  async listSavedWallets(organizationId?: string | null): Promise<OtcSavedWallet[]> {
    const { data } = await HttpApiService.get<{ wallets?: OtcSavedWallet[] }>(
      `${BASE_URL}/saved-wallets${buildQuery({ organization_id: organizationId })}`,
    );
    return Array.isArray(data?.wallets) ? data.wallets : [];
  },

  async createSavedWallet(
    payload: CreateOtcSavedWalletPayload,
    twoFactorCode: string,
  ): Promise<OtcSavedWallet> {
    const { data } = await HttpApiService.post<OtcSavedWallet>(`${BASE_URL}/saved-wallets`, payload, {
      headers: { "x-two-factor-code": twoFactorCode },
    });
    return data;
  },

  async deleteSavedWallet(id: string, organizationId?: string | null): Promise<void> {
    await HttpApiService.delete(
      `${BASE_URL}/saved-wallets/${encodeURIComponent(id)}${buildQuery({ organization_id: organizationId })}`,
    );
  },
};
